import type { MatchSnapshot } from "@arcade-hockey/sim";
import { tuning } from "@arcade-hockey/sim";

// The follow camera's output: a smoothed center point in SIM space. The
// CameraRig turns this into an actual perspective pose; this module owns
// only the chase behavior (dead zone, damping, attack bias, lookahead).
export type FollowCamera = {
  centerWorld: { x: number; y: number };
};

export type CameraConfig = {
  deadZoneHalfX: number; // world units; +x is sim length axis
  deadZoneHalfY: number;
  smoothTimeSec: number; // critically-damped smoothing time
  attackBiasFraction: number; // 0..1 toward attacking goal
  lookaheadSeconds: number;
};

export const defaultCameraConfig: CameraConfig = {
  deadZoneHalfX: 4.75,
  deadZoneHalfY: 2.4,
  smoothTimeSec: 0.12,
  attackBiasFraction: 0.12,
  lookaheadSeconds: 0.18,
};

/**
 * Critically-damped smoothing toward a target. Mutates currentPos/currentVel in place.
 * Standard "smoothdamp" recipe (Unity-style).
 */
function smoothDamp(
  current: number,
  target: number,
  vel: { v: number },
  smoothTime: number,
  dt: number,
): number {
  const st = Math.max(0.0001, smoothTime);
  const omega = 2 / st;
  const x = omega * dt;
  const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);
  const change = current - target;
  const temp = (vel.v + omega * change) * dt;
  vel.v = (vel.v - omega * temp) * exp;
  return target + (change + temp) * exp;
}

export class GameCamera {
  private cam: FollowCamera;
  private velX = { v: 0 };
  private velY = { v: 0 };

  constructor(
    initial: FollowCamera = { centerWorld: { x: 0, y: 0 } },
    private config = defaultCameraConfig,
  ) {
    this.cam = { centerWorld: { ...initial.centerWorld } };
  }

  /**
   * Returns the live internal camera object. Callers must treat the result as
   * read-only — mutating it corrupts subsequent frames. The shared reference
   * keeps the per-frame render loop allocation-free.
   */
  current(): FollowCamera {
    return this.cam;
  }

  update(snap: MatchSnapshot, controlledPlayerId: string | null, dt: number): FollowCamera {
    // Compute target from weighted blend.
    const halfL = tuning.rink.length / 2;
    const goalLineOffset = tuning.rink.goalLineOffset;

    const puckPos = snap.puck.pos;
    const player =
      controlledPlayerId === null ? null : snap.players.find((p) => p.id === controlledPlayerId);
    const playerPos = player?.pos ?? puckPos;

    // Attacking goal — biased toward the direction the puck CARRIER
    // is attacking, not statically toward HOME's attacking goal. This
    // matters when AWAY has the puck (especially during a shootout
    // attempt by the away team): without an adaptive bias the camera
    // pulls toward HOME's attacking goal (+x) while the puck travels
    // -x, so the action escapes the dead zone and the shooter goes
    // off-screen. Falls back to HOME's attacking direction (+x) when
    // the puck is loose / unowned, matching pre-fix behaviour.
    let attackingDirX = 1;
    if (snap.puck.ownerId !== null) {
      const owner = snap.players.find((p) => p.id === snap.puck.ownerId);
      if (owner !== undefined) attackingDirX = owner.teamId === "home" ? 1 : -1;
    } else if (snap.puck.lastTouchedBy !== null) {
      const last = snap.players.find((p) => p.id === snap.puck.lastTouchedBy);
      if (last !== undefined) attackingDirX = last.teamId === "home" ? 1 : -1;
    }
    const attackingGoal = { x: attackingDirX * (halfL - goalLineOffset), y: 0 };

    // Lookahead from puck velocity.
    const lookaheadX = snap.puck.vel.x * this.config.lookaheadSeconds;
    const lookaheadY = snap.puck.vel.y * this.config.lookaheadSeconds;

    const targetX =
      puckPos.x * 0.55 +
      playerPos.x * 0.25 +
      attackingGoal.x * this.config.attackBiasFraction +
      lookaheadX * 0.22;
    const targetY =
      puckPos.y * 0.55 +
      playerPos.y * 0.25 +
      attackingGoal.y * this.config.attackBiasFraction +
      lookaheadY * 0.22;

    // Dead zone: only move camera if target exits the box.
    const dx = targetX - this.cam.centerWorld.x;
    const dy = targetY - this.cam.centerWorld.y;
    let desiredX = this.cam.centerWorld.x;
    let desiredY = this.cam.centerWorld.y;
    if (Math.abs(dx) > this.config.deadZoneHalfX) {
      desiredX = targetX - Math.sign(dx) * this.config.deadZoneHalfX;
    }
    if (Math.abs(dy) > this.config.deadZoneHalfY) {
      desiredY = targetY - Math.sign(dy) * this.config.deadZoneHalfY;
    }

    // Critically-damped pursuit.
    this.cam.centerWorld.x = smoothDamp(
      this.cam.centerWorld.x,
      desiredX,
      this.velX,
      this.config.smoothTimeSec,
      dt,
    );
    this.cam.centerWorld.y = smoothDamp(
      this.cam.centerWorld.y,
      desiredY,
      this.velY,
      this.config.smoothTimeSec,
      dt,
    );

    // Clamp to rink interior (don't show beyond the boards).
    const halfWidth = tuning.rink.width / 2;
    this.cam.centerWorld.x = Math.max(-halfL + 1, Math.min(halfL - 1, this.cam.centerWorld.x));
    this.cam.centerWorld.y = Math.max(
      -halfWidth + 1,
      Math.min(halfWidth - 1, this.cam.centerWorld.y),
    );

    return this.current();
  }
}
